import { useState } from "react"
import { Box, Card, IconButton, Popover, TextField, Typography } from "@mui/material"
import TipTapEditor from "@crm-src/components/tiptap-editor"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { Calendar, Palette } from "lucide-react"
import { WebinarSection } from "./stock-images"

// Editor UI for the webinar details section: title, tagline, date/time/duration
// inputs, location URL, registration link, and a TipTap rich-text description.
// The corresponding email HTML is built by `webinarDetailsToHtml` below — it
// also inlines the speakers HTML block between the event cards and the
// description, since the speakers section never renders standalone in email.
interface WebinarDetailsSectionProps {
  section: WebinarSection
  bgColor: string
  onUpdate: (sectionId: string, partial: any) => void
  onChangeBgColor: (sectionId: string, color: string) => void
}

export function WebinarDetailsSection({
  section,
  bgColor,
  onUpdate,
  onChangeBgColor,
}: WebinarDetailsSectionProps) {
  const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null)
  return (
    <Card key={section.id} sx={{ borderRadius: 0 }} style={{ backgroundColor: bgColor, marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <Calendar size={20} />
            Webinar Details
          </Typography>
          <IconButton size="small" onClick={(e) => setColorAnchor(e.currentTarget)}>
            <Palette size={16} />
          </IconButton>
          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { width: 256, p: 2 } } }}
          >
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Background Color</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {['#ffffff', '#f8fafc', '#eff6ff', '#f0f9ff', '#ecfdf5'].map(color => (
                <Box
                  component="button"
                  key={color}
                  sx={{ width: 32, height: 32, borderRadius: 1, border: "2px solid", cursor: "pointer" }}
                  style={{ backgroundColor: color, borderColor: bgColor === color ? '#2563eb' : '#e5e7eb' }}
                  onClick={() => onChangeBgColor(section.id, color)}
                />
              ))}
            </Box>
          </Popover>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`title-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Webinar Title</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="webinar title - create a professional and engaging webinar title that highlights the value"
                  placeholder="e.g., mastering CRM best practices"
                  currentValue={section.content.title}
                  onGenerate={(generated) => onUpdate(section.id, { title: generated })}
                />
                <AiTextImprover
                  fieldType="short"
                  context="webinar title"
                  currentText={section.content.title}
                  onImprove={(improved) => onUpdate(section.id, { title: improved })}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              id={`title-${section.id}`}
              value={section.content.title}
              onChange={(e) => onUpdate(section.id, { title: e.target.value })}
              placeholder="Webinar title"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`tagline-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Tagline</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="webinar tagline - a short catchy line shown below the title to encourage attendance"
                  placeholder="e.g., join us for an informative discussion"
                  currentValue={section.content.tagline}
                  onGenerate={(generated) => onUpdate(section.id, { tagline: generated })}
                />
                <AiTextImprover
                  fieldType="short"
                  context="webinar tagline"
                  currentText={section.content.tagline}
                  onImprove={(improved) => onUpdate(section.id, { tagline: improved })}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              id={`tagline-${section.id}`}
              value={section.content.tagline || ''}
              onChange={(e) => onUpdate(section.id, { tagline: e.target.value })}
              placeholder="Join us for an insightful session"
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
            <Box>
              <Typography component="label" htmlFor={`date-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Date</Typography>
              <TextField
                fullWidth
                size="small"
                id={`date-${section.id}`}
                value={section.content.date}
                onChange={(e) => onUpdate(section.id, { date: e.target.value })}
                placeholder="March 15, 2025"
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor={`time-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Time</Typography>
              <TextField
                fullWidth
                size="small"
                id={`time-${section.id}`}
                value={section.content.time}
                onChange={(e) => onUpdate(section.id, { time: e.target.value })}
                placeholder="2:00 PM EST"
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor={`duration-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Duration</Typography>
              <TextField
                fullWidth
                size="small"
                id={`duration-${section.id}`}
                value={section.content.duration}
                onChange={(e) => onUpdate(section.id, { duration: e.target.value })}
                placeholder="60 minutes"
              />
            </Box>
          </Box>

          <Box>
            <Typography component="label" htmlFor={`location-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Location (URL)</Typography>
            <TextField
              fullWidth
              size="small"
              id={`location-${section.id}`}
              value={section.content.location || ''}
              onChange={(e) => onUpdate(section.id, { location: e.target.value })}
              placeholder="https://zoom.us/j/your-meeting-id"
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor={`registration-link-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Registration Link</Typography>
            <TextField
              fullWidth
              size="small"
              id={`registration-link-${section.id}`}
              value={section.content.registrationLink || ''}
              onChange={(e) => onUpdate(section.id, { registrationLink: e.target.value })}
              placeholder="https://your-site.com/webinar-registration"
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Link where recipients can register for the webinar</Typography>
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Description</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="webinar description - write a detailed description explaining what attendees will learn and why they should attend"
                  placeholder="e.g., explain the key topics and benefits"
                  currentValue={section.content.description.replace(/<[^>]*>/g, '')}
                  onGenerate={(generated) => onUpdate(section.id, { description: `<p>${generated}</p>` })}
                />
                <AiTextImprover
                  fieldType="long"
                  context="webinar description"
                  currentText={section.content.description.replace(/<[^>]*>/g, '')}
                  onImprove={(improved) => onUpdate(section.id, { description: `<p>${improved}</p>` })}
                />
              </Box>
            </Box>
            <TipTapEditor
              value={section.content.description}
              onChange={(html) => onUpdate(section.id, { description: html })}
              placeholder="Describe what attendees will learn and why they should attend..."
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Build the email HTML for the webinar details section. The `speakersHtml`
// argument is spliced in between the event cards and the About description so
// speakers visually anchor to the same surface as the rest of the details.
// The fourth event card switches based on which is set:
//   * registrationLink → "REGISTER" card with link to register
//   * else if location → "JOIN" card with link to join meeting
//   * else neither → no fourth card
// The Register Now CTA below the description only appears when registrationLink is set.
export function webinarDetailsToHtml(section: WebinarSection, speakersHtml: string): string {
  // Extract content with fallback defaults to prevent empty values
  const webinarTitle = section.content.title || 'Upcoming Webinar'
  const webinarTagline = section.content.tagline || 'Join us for an insightful session'
  const webinarDate = section.content.date || 'TBD'
  const webinarTime = section.content.time || 'TBD'
  const webinarDuration = section.content.duration || '60 minutes'
  const webinarLocation = section.content.location || ''
  const webinarRegistrationLink = section.content.registrationLink || ''
  const webinarDescription = section.content.description || '<p>Join us for this informative session.</p>'

  return `
          <!-- Webinar Details -->
          <tr>
            <td style="padding: 0;">
              <!-- Webinar Title Section -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#f8f9fa" style="background-color: #f8f9fa;">
                <tr>
                  <td class="mobile-pad" style="padding: 30px 40px; text-align: center;">
                    <h2 style="color: #2d3748; font-size: 24px; font-weight: bold; margin: 0 0 12px 0; font-family: Arial, Helvetica, sans-serif; line-height: 30px;">${webinarTitle}</h2>
                    <p style="color: #718096; font-size: 15px; font-weight: bold; margin: 12px 0 0 0; font-family: Arial, Helvetica, sans-serif; line-height: 22px;">${webinarTagline}</p>
                  </td>
                </tr>
              </table>

              <!-- Event Details Section -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#f7fafc" style="background-color: #f7fafc;">
                <tr>
                  <td style="padding: 12px 20px;">
                    <!-- Event Cards -->
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <!-- Date Card -->
                        <td class="mobile-stack" style="padding: 5px; width: 25%; vertical-align: top;" align="center" valign="top">
                          <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#667eea" style="background-color: #667eea; border-radius: 8px;">
                            <tr>
                              <td style="padding: 16px 8px; text-align: center; vertical-align: middle; height: 70px;" height="70" valign="middle" align="center">
                                <p style="color: #e6e9ff; font-size: 10px; font-weight: bold; margin: 0 0 6px 0; line-height: 14px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">DATE</p>
                                <p style="color: #ffffff; font-size: 13px; font-weight: bold; margin: 0; line-height: 18px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">${webinarDate}</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <!-- Time Card -->
                        <td class="mobile-stack" style="padding: 5px; width: 25%; vertical-align: top;" align="center" valign="top">
                          <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#e53e3e" style="background-color: #e53e3e; border-radius: 8px;">
                            <tr>
                              <td style="padding: 16px 8px; text-align: center; vertical-align: middle; height: 70px;" height="70" valign="middle" align="center">
                                <p style="color: #ffe6e6; font-size: 10px; font-weight: bold; margin: 0 0 6px 0; line-height: 14px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">TIME</p>
                                <p style="color: #ffffff; font-size: 13px; font-weight: bold; margin: 0; line-height: 18px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">${webinarTime}</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <!-- Duration Card -->
                        <td class="mobile-stack" style="padding: 5px; width: 25%; vertical-align: top;" align="center" valign="top">
                          <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#3182ce" style="background-color: #3182ce; border-radius: 8px;">
                            <tr>
                              <td style="padding: 16px 8px; text-align: center; vertical-align: middle; height: 70px;" height="70" valign="middle" align="center">
                                <p style="color: #cce5ff; font-size: 10px; font-weight: bold; margin: 0 0 6px 0; line-height: 14px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">DURATION</p>
                                <p style="color: #ffffff; font-size: 13px; font-weight: bold; margin: 0; line-height: 18px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">${webinarDuration}</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        ${webinarRegistrationLink ? `
                        <!-- Register Card -->
                        <td class="mobile-stack" style="padding: 5px; width: 25%; vertical-align: top;" align="center" valign="top">
                          <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#667eea" style="background-color: #667eea; border-radius: 8px;">
                            <tr>
                              <td style="padding: 16px 8px; text-align: center; vertical-align: middle; height: 70px;" height="70" valign="middle" align="center">
                                <p style="color: #e6e9ff; font-size: 10px; font-weight: bold; margin: 0 0 6px 0; line-height: 14px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">REGISTER</p>
                                <p style="margin: 0; line-height: 18px; mso-line-height-rule: exactly;"><a href="${webinarRegistrationLink}" style="color: #ffffff; font-size: 13px; font-weight: bold; text-decoration: underline; font-family: Arial, Helvetica, sans-serif;">Link</a></p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        ` : webinarLocation ? `
                        <!-- Join Card -->
                        <td class="mobile-stack" style="padding: 5px; width: 25%; vertical-align: top;" align="center" valign="top">
                          <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#38a169" style="background-color: #38a169; border-radius: 8px;">
                            <tr>
                              <td style="padding: 16px 8px; text-align: center; vertical-align: middle; height: 70px;" height="70" valign="middle" align="center">
                                <p style="color: #d4f4dd; font-size: 10px; font-weight: bold; margin: 0 0 6px 0; line-height: 14px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">JOIN</p>
                                <p style="margin: 0; line-height: 18px; mso-line-height-rule: exactly;"><a href="${webinarLocation}" style="color: #ffffff; font-size: 13px; font-weight: bold; text-decoration: underline; font-family: Arial, Helvetica, sans-serif;">Link</a></p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        ` : ''}
                      </tr>
                    </table>
                  </td>
                </tr>

                ${speakersHtml}

                <!-- Description -->
                <tr>
                  <td class="mobile-pad" style="padding: 4px 20px 25px 20px;" align="center">
                    <table class="mobile-full" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="background-color: #ffffff; border: 1px solid #e2e8f0;">
                      <tr>
                        <td style="padding: 25px; font-family: Arial, Helvetica, sans-serif;">
                          <h3 style="color: #2d3748; font-size: 16px; font-weight: bold; margin: 0 0 12px 0; font-family: Arial, Helvetica, sans-serif; line-height: 22px;">${'About This Webinar'}</h3>
                          <table width="100%" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="color: #4a5568; font-size: 14px; line-height: 22px; mso-line-height-rule: exactly; font-family: Arial, Helvetica, sans-serif;">
                                ${webinarDescription}
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                ${webinarRegistrationLink ? `
                <!-- Registration Button (Bulletproof for Outlook) -->
                <tr>
                  <td style="padding: 10px 40px 30px 40px;" align="center">
                    <!--[if mso]>
                    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${webinarRegistrationLink}" style="height:48px;v-text-anchor:middle;width:220px;" arcsize="13%" strokecolor="#667eea" fillcolor="#667eea">
                      <w:anchorlock/>
                      <center style="color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;">Register Now</center>
                    </v:roundrect>
                    <![endif]-->
                    <!--[if !mso]><!-->
                    <table cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td align="center" bgcolor="#667eea" style="background-color: #667eea; border-radius: 6px; padding: 14px 40px;">
                          <a href="${webinarRegistrationLink}" target="_blank" style="color: #ffffff; font-size: 16px; font-weight: bold; text-decoration: none; font-family: Arial, Helvetica, sans-serif; display: inline-block; line-height: 20px;">Register Now</a>
                        </td>
                      </tr>
                    </table>
                    <!--<![endif]-->
                  </td>
                </tr>
                ` : ''}
              </table>
            </td>
          </tr>
          `
}
